import { useCallback, useEffect, useState, useMemo, } from 'react';
import Cookies from 'js-cookie';
import debounce from 'lodash/debounce';
import lowerCase from 'lodash/lowerCase';
import { useHistory } from 'react-router-dom';
import { useOutsideClick, useWindowSize } from '@yme/common/hooks';
import { setHeaderSearchShown, getHeaderSearchShown, setIsMenuActive, } from '@yme/components/Header/redux';
import { useDispatch } from '@yme/hooks/useDispatch';
import { useSelector } from '@yme/hooks/useSelector';
import { getBloomreachModel, getIsInAuthor, getSearchResultsPageUrl } from '@yme/selectors/common';
import { Network } from '@yme/services/network';
import { BR_ID_COOKIE, FacetFields, FlParams, getPrimaryTypeFacet, getSearchParams, PixelEvent, RequestType, SearchType, setPixelEvent, } from '@yme/utils/bloomreach';
import { keyCodes } from '@yme/utils/constants';
import { DEBOUNCE_TIME, MAX_SEARCH_CONTENT_ITEMS, MIN_SYMBOLS_TO_SEARCH } from './constants';
import { generateRandomDigitNumber, getActiveElementUrl, getQueryFromActiveElementData, isFacetActiveElement, shouldHandleEvent, sortCategoriesByPriority, } from './helpers';
import { useSearchHistory } from './useSearchHistory';
export const useSearch = (isSearchPage = false, autoCorrectQueryResult = '') => {
    const { query } = getSearchParams();
    const { isInfiniteDesktopView } = useWindowSize();
    const [inputValue, setInputValue] = useState('');
    const [typeFacets, setTypeFacets] = useState([]);
    const [isSearchCalled, setIsSearchCalled] = useState(false);
    const [activeElement, setActiveElement] = useState(-1);
    const [querySuggestions, setQuerySuggestions] = useState([]);
    const [productSearchSuggestions, setProductSearchSuggestions] = useState([]);
    const [contentSearchSuggestions, setContentSearchSuggestions] = useState([]);
    const [searchedValue, setSearchedValue] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [autoCorrectQuery, setAutoCorrectQuery] = useState('');
    const isHeaderSearchOpen = useSelector(getHeaderSearchShown);
    const { autosuggestHost, searchHost, accountId, productCatalog, contentCatalog } = useSelector(getBloomreachModel);
    const searchResultsPageUrl = useSelector(getSearchResultsPageUrl);
    const isInAuthor = useSelector(getIsInAuthor);
    const dispatch = useDispatch();
    const history = useHistory();
    const isResultsExisted = Boolean(typeFacets.length);
    const { handleAddToSearchHistory, searchHistoryResult, isSearchHistoryExisted } = useSearchHistory();
    const trimmedInputValue = inputValue.trim();
    const isOpen = isSearchPage ? isSearchOpen : isHeaderSearchOpen;
    const isShowNoResults = isOpen &&
        isSearchCalled &&
        !isResultsExisted &&
        trimmedInputValue.length >= MIN_SYMBOLS_TO_SEARCH &&
        !autoCorrectQuery;
    const br_uid = Cookies.get(BR_ID_COOKIE);
    const sortedTypeFacets = useMemo(() => sortCategoriesByPriority(typeFacets).slice(0, MAX_SEARCH_CONTENT_ITEMS), [typeFacets]);
    const results = useMemo(() => {
        if (inputValue.length < MIN_SYMBOLS_TO_SEARCH && searchHistoryResult.length) {
            return searchHistoryResult;
        }
        return [
            ...querySuggestions,
            ...productSearchSuggestions,
            ...contentSearchSuggestions,
            ...sortedTypeFacets,
        ];
    }, [
        contentSearchSuggestions,
        inputValue,
        productSearchSuggestions,
        querySuggestions,
        searchHistoryResult,
        sortedTypeFacets,
    ]);
    const onSearchClickHandler = useCallback(() => {
        if (isSearchPage) {
            setIsSearchOpen((prev) => !prev);
        }
        else {
            dispatch(setHeaderSearchShown(!isHeaderSearchOpen));
        }
    }, [dispatch, isHeaderSearchOpen, isSearchPage]);
    const onSearchCloseHandler = useCallback(() => {
        if (isSearchPage) {
            setIsSearchOpen(false);
        }
        else {
            dispatch(setHeaderSearchShown(false));
        }
    }, [dispatch, isSearchPage]);
    const onFocusHandler = useCallback(() => {
        if (isSearchPage) {
            setIsSearchOpen(true);
        }
        else {
            dispatch(setHeaderSearchShown(true));
        }
    }, [dispatch, isSearchPage]);
    const { elementRef } = useOutsideClick({
        eventName: 'click',
        callback: onSearchCloseHandler,
    });
    const handleBlurInput = useCallback(() => {
        elementRef.current?.querySelector('input')?.blur();
    }, [elementRef]);
    const getElementIndexUp = useCallback(() => {
        return activeElement > 0 ? activeElement - 1 : results.length - 1;
    }, [activeElement, results.length]);
    const getElementIndexDown = useCallback(() => {
        return activeElement < results.length - 1 ? activeElement + 1 : 0;
    }, [activeElement, results.length]);
    const handleQuerySuggestionHint = useCallback((event) => {
        if (event.key === keyCodes.ARROW_DOWN || event.key === keyCodes.ARROW_UP) {
            event.preventDefault();
            const index = event.key === keyCodes.ARROW_DOWN ? getElementIndexDown() : getElementIndexUp();
            if (results[index] && 'displayText' in results[index]) {
                const element = results[index];
                setInputValue(element.displayText);
            }
            else if (inputValue !== searchedValue &&
                trimmedInputValue.length > MIN_SYMBOLS_TO_SEARCH) {
                setInputValue(searchedValue);
            }
        }
    }, [
        getElementIndexDown,
        getElementIndexUp,
        results,
        inputValue,
        searchedValue,
        trimmedInputValue.length,
    ]);
    const handleSearchPixelEvent = useCallback((activeElement, activeElementData, trimmedInputValue) => {
        if (activeElement === -1 ||
            isFacetActiveElement(activeElementData) ||
            !trimmedInputValue.length) {
            setPixelEvent(PixelEvent.SEARCH, {
                q: getQueryFromActiveElementData(activeElementData, trimmedInputValue),
                domain_key: productCatalog,
            });
        }
        else {
            setPixelEvent(PixelEvent.SUGGEST, {
                aq: trimmedInputValue,
                q: getQueryFromActiveElementData(activeElementData, trimmedInputValue),
                domain_key: productCatalog,
            });
        }
    }, [productCatalog]);
    const handleActiveElement = useCallback((event) => {
        if (event.key === keyCodes.ARROW_UP) {
            setActiveElement(getElementIndexUp());
        }
        else if (event.key === keyCodes.ARROW_DOWN) {
            setActiveElement(getElementIndexDown());
        }
        else if (shouldHandleEvent({
            eventKey: event.key,
            trimmedInputValue,
            isShowNoResults,
            searchHistoryResult,
            activeElement,
        })) {
            onSearchCloseHandler();
            handleBlurInput();
            handleAddToSearchHistory(trimmedInputValue);
            const activeElementData = results[activeElement] ?? {};
            if (!isInAuthor) {
                handleSearchPixelEvent(activeElement, activeElementData, trimmedInputValue);
            }
            history.push(getActiveElementUrl(activeElementData, searchResultsPageUrl, trimmedInputValue));
            if (!isInfiniteDesktopView) {
                dispatch(setIsMenuActive(false));
            }
        }
    }, [
        trimmedInputValue,
        isShowNoResults,
        searchHistoryResult,
        getElementIndexUp,
        getElementIndexDown,
        onSearchCloseHandler,
        handleBlurInput,
        handleAddToSearchHistory,
        results,
        activeElement,
        isInAuthor,
        history,
        searchResultsPageUrl,
        isInfiniteDesktopView,
        handleSearchPixelEvent,
        dispatch,
    ]);
    const onKeyDownHandler = useCallback((event) => {
        handleQuerySuggestionHint(event);
        handleActiveElement(event);
    }, [handleQuerySuggestionHint, handleActiveElement]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === keyCodes.ESCAPE) {
            event.preventDefault();
            if (isSearchPage) {
                handleBlurInput();
                setIsSearchOpen(false);
            }
            else {
                dispatch(setHeaderSearchShown(false));
            }
        }
    }, [dispatch, handleBlurInput, isSearchPage]);
    const getSearchData = useMemo(() => debounce(async (query) => {
        const commonParams = {
            _br_uid_2: br_uid,
            account_id: accountId,
            'facet.field': FacetFields.PRIMARY_TYPE,
            q: query,
            ref_url: location.href,
            request_type: RequestType.SEARCH,
            search_type: SearchType.KEYWORD,
            start: 0,
            url: location.href,
        };
        const suggestParams = {
            _br_uid_2: br_uid,
            account_id: accountId,
            catalog_views: `${productCatalog}|${contentCatalog}`,
            q: query,
            ref_url: location.href,
            request_id: generateRandomDigitNumber(),
            request_type: RequestType.SUGGEST,
            url: location.href,
        };
        const suggestRequest = () => Network.get(autosuggestHost, {
            params: {
                ...suggestParams,
            },
        });
        const productRequest = () => Network.get(searchHost, {
            params: {
                ...commonParams,
                fl: `${FlParams.PID},${FlParams.TITLE},${FlParams.URL}`,
                domain_key: productCatalog,
            },
        });
        const contentRequest = () => Network.get(searchHost, {
            params: {
                ...commonParams,
                fl: `${FlParams.ITEM_ID},${FlParams.TITLE},${FlParams.URL}`,
                catalog_name: contentCatalog,
            },
        });
        try {
            const [suggestAnswer, productAnswer, contentAnswer] = await Promise.allSettled([
                suggestRequest(),
                productRequest(),
                contentRequest(),
            ]);
            let primaryTypeProduct = [];
            let primaryTypeContent = [];
            if (suggestAnswer.status === 'fulfilled') {
                const { suggestionGroups } = suggestAnswer.value.data;
                const productSuggestionGroup = suggestionGroups.find((group) => lowerCase(group.catalogName) === lowerCase(productCatalog));
                const contentSuggestionGroup = suggestionGroups.find((group) => lowerCase(group.catalogName) === lowerCase(contentCatalog));
                const productQuerySuggestions = productSuggestionGroup?.querySuggestions ?? [];
                const contentQuerySuggestions = contentSuggestionGroup?.querySuggestions ?? [];
                const showedQuerySuggestions = [
                    ...productQuerySuggestions,
                    ...contentQuerySuggestions,
                ].slice(0, MAX_SEARCH_CONTENT_ITEMS);
                const showedProductSearchSuggestions = (productSuggestionGroup?.searchSuggestions ?? []).slice(0, MAX_SEARCH_CONTENT_ITEMS);
                const showedContentSearchSuggestions = (contentSuggestionGroup?.searchSuggestions ?? []).slice(0, MAX_SEARCH_CONTENT_ITEMS);
                setQuerySuggestions(showedQuerySuggestions);
                setProductSearchSuggestions(showedProductSearchSuggestions);
                setContentSearchSuggestions(showedContentSearchSuggestions);
            }
            if (productAnswer.status === 'fulfilled') {
                primaryTypeProduct = getPrimaryTypeFacet(productAnswer.value.data.facet_counts.facets);
                setAutoCorrectQuery(productAnswer.value.data.autoCorrectQuery ?? '');
            }
            if (contentAnswer.status === 'fulfilled') {
                primaryTypeContent = getPrimaryTypeFacet(contentAnswer.value.data.facet_counts.facets);
            }
            setIsSearchCalled(true);
            setTypeFacets([...primaryTypeProduct, ...primaryTypeContent]);
            setActiveElement(-1);
            setSearchedValue(query);
        }
        catch (error) {
            console.error(error);
        }
    }, DEBOUNCE_TIME, {
        leading: isSearchPage,
    }), [accountId, autosuggestHost, br_uid, contentCatalog, isSearchPage, productCatalog, searchHost]);
    const handleSetInputValue = useCallback((value) => {
        const trimmedValue = value.trim();
        if (trimmedValue.length >= MIN_SYMBOLS_TO_SEARCH) {
            getSearchData(trimmedValue);
        }
        setInputValue(value);
    }, [getSearchData]);
    const handleReset = useCallback(() => {
        setIsSearchCalled(false);
        setTypeFacets([]);
        setQuerySuggestions([]);
        setProductSearchSuggestions([]);
        setActiveElement(-1);
        setSearchedValue('');
        setAutoCorrectQuery('');
    }, []);
    useEffect(() => {
        return () => {
            getSearchData.cancel();
        };
    }, [getSearchData]);
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
    useEffect(() => {
        if (isSearchPage) {
            setInputValue(autoCorrectQueryResult || query);
            handleReset();
        }
    }, [query, isSearchPage, handleReset, elementRef, autoCorrectQueryResult]);
    useEffect(() => {
        if (trimmedInputValue.length < MIN_SYMBOLS_TO_SEARCH) {
            handleReset();
        }
    }, [handleReset, trimmedInputValue]);
    return {
        isSearchOpen,
        isHeaderSearchOpen,
        isShowNoResults,
        isResultsExisted,
        isSearchHistoryExisted,
        inputValue,
        trimmedInputValue,
        querySuggestions,
        productSearchSuggestions,
        contentSearchSuggestions,
        sortedTypeFacets,
        searchedValue,
        searchResultsPageUrl,
        activeElement,
        elementRef,
        query,
        autoCorrectQuery,
        setInputValue,
        onSearchClickHandler,
        onSearchCloseHandler,
        onFocusHandler,
        handleSetInputValue,
        onKeyDownHandler,
    };
};
